import { gql } from '@apollo/client';

// Everything the detail page reads. Every mutation returns it, so the page never refetches after a write.
export const PUSH_BROADCAST_DATA = gql`
  fragment PushBroadcastData on PushBroadcast {
    id
    name
    status
    revision
    contentVersion
    filtersVersion
    titleTexts
    bodyTexts
    destination {
      type
      entityId
      label
      caveat
    }
    settingsCategory
    filters {
      daysSinceLastOnline
      platform
      appVersion
      daysSinceInstall
      minLevel
      spendTiers
      gameLanguages
      userIds
      excludedUserIds
    }
    audience {
      count
      byLanguage {
        language
        count
        hasTranslation
      }
      receivingEnglishFallback
      computedAt
      computedBy
      filtersVersion
      isCurrent
    }
    isAudienceCurrent
    startBlockers
    warnings {
      language
      field
      message
    }
    duplicatedFromId
    resendWarning {
      sourceBroadcastId
      sourceName
      userIds
      message
    }
    progress {
      batchesTotal
      batchesPending
      batchesRunning
      batchesCompleted
      batchesFailed
      batchesCancelled
      recipientsSent
      devicesTargeted
      devicesAccepted
      devicesRejected
      devicesPending
      tokensPruned
    }
    sendAt
    isScheduleOverdue
    recipientsCount
    lastError
    createdBy
    createdAt
    updatedBy
    updatedAt
    startedBy
    startedAt
    cancelledBy
    cancelledAt
    completedAt
  }
`;

// The list row: no progress or resend warning (each costs a query per row on the server).
export const PUSH_BROADCAST_LIST_DATA = gql`
  fragment PushBroadcastListData on PushBroadcast {
    id
    name
    status
    recipientsCount
    audience {
      count
      isCurrent
    }
    sendAt
    isScheduleOverdue
    duplicatedFromId
    createdBy
    createdAt
    startedBy
    startedAt
    cancelledBy
    completedAt
  }
`;

export const GET_PUSH_BROADCASTS = gql`
  query getPushBroadcasts($limit: Int, $offset: Int) {
    pushBroadcasts(limit: $limit, offset: $offset) {
      ...PushBroadcastListData
    }
  }
  ${PUSH_BROADCAST_LIST_DATA}
`;

export const GET_PUSH_BROADCAST = gql`
  query getPushBroadcast($id: ID!) {
    pushBroadcast(id: $id) {
      ...PushBroadcastData
    }
  }
  ${PUSH_BROADCAST_DATA}
`;

// What the editor may offer: nothing of it is hard-coded in the admin.
export const GET_PUSH_BROADCAST_OPTIONS = gql`
  query getPushBroadcastOptions {
    pushBroadcastLanguages {
      code
      name
      isSource
    }
    pushBroadcastLimits {
      titleMaxLength
      bodyMaxLength
      titleWarnLength
      bodyWarnLength
      nameMaxLength
      defaultDaysSinceLastOnline
      maxDaysSinceLastOnline
      maxListedUsers
      placeholders
    }
    pushBroadcastSettingsCategories {
      category
      label
      description
    }
    pushBroadcastDestinations {
      type
      entityId
      label
      caveat
    }
  }
`;

// Who created, started, counted: the broadcast holds user ids only.
export const GET_PUSH_BROADCAST_PEOPLE = gql`
  query getPushBroadcastPeople($ids: [String!]!) {
    users(properties: $ids, type: BY_ID) {
      id
      displayName
      emailCredential
    }
  }
`;

export const CREATE_PUSH_BROADCAST = gql`
  mutation createPushBroadcast($input: PushBroadcastInput!) {
    createPushBroadcast(input: $input) {
      ...PushBroadcastData
    }
  }
  ${PUSH_BROADCAST_DATA}
`;

export const UPDATE_PUSH_BROADCAST = gql`
  mutation updatePushBroadcast($id: ID!, $expectedVersion: Int!, $input: PushBroadcastInput!) {
    updatePushBroadcast(id: $id, expectedVersion: $expectedVersion, input: $input) {
      ...PushBroadcastData
    }
  }
  ${PUSH_BROADCAST_DATA}
`;

export const DELETE_PUSH_BROADCAST = gql`
  mutation deletePushBroadcast($id: ID!) {
    deletePushBroadcast(id: $id)
  }
`;

// The push as a player of one language receives it, rendered by the send's own code (unsaved texts allowed).
export const GET_PUSH_BROADCAST_PREVIEW = gql`
  query getPushBroadcastPreview($id: ID!, $language: String!, $titleTexts: JSON, $bodyTexts: JSON) {
    pushBroadcastPreview(id: $id, language: $language, titleTexts: $titleTexts, bodyTexts: $bodyTexts) {
      requestedLanguage
      language
      isFallback
      title
      body
      sampleValues {
        placeholder
        value
        isSample
      }
      surfaces {
        surface
        label
        note
        title {
          shown
          isCut
          limit
          length
        }
        body {
          shown
          isCut
          limit
          length
        }
      }
      errors {
        language
        field
        message
      }
      warnings {
        language
        field
        message
      }
    }
  }
`;

export const GET_PUSH_BROADCAST_TRANSLATION_TEMPLATE = gql`
  query getPushBroadcastTranslationTemplate($id: ID!) {
    pushBroadcastTranslationTemplate(id: $id)
  }
`;

// The count: on the Count audience button only, never on edit or on load.
export const PREVIEW_PUSH_BROADCAST_AUDIENCE = gql`
  mutation previewPushBroadcastAudience($id: ID!) {
    previewPushBroadcastAudience(id: $id) {
      ...PushBroadcastData
    }
  }
  ${PUSH_BROADCAST_DATA}
`;

// Reads a filled template back: the diff and every error, nothing applied.
export const PREVIEW_PUSH_BROADCAST_TRANSLATION_IMPORT = gql`
  mutation previewPushBroadcastTranslationImport($id: ID!, $file: Upload!) {
    previewPushBroadcastTranslationImport(id: $id, file: $file) {
      ok
      changes {
        language
        field
        kind
        before
        after
      }
      errors {
        row
        language
        field
        message
      }
      warnings {
        row
        language
        field
        message
      }
      titleTexts
      bodyTexts
      expectedVersion
    }
  }
`;

// sendAt null = now; otherwise a UTC time at most 30 days ahead (every check runs again when it fires).
export const START_PUSH_BROADCAST = gql`
  mutation startPushBroadcast($id: ID!, $expectedVersion: Int!, $sendAt: Date) {
    startPushBroadcast(id: $id, expectedVersion: $expectedVersion, sendAt: $sendAt) {
      ...PushBroadcastData
    }
  }
  ${PUSH_BROADCAST_DATA}
`;

export const CANCEL_PUSH_BROADCAST = gql`
  mutation cancelPushBroadcast($id: ID!) {
    cancelPushBroadcast(id: $id) {
      ...PushBroadcastData
    }
  }
  ${PUSH_BROADCAST_DATA}
`;

export const RETRY_FAILED_PUSH_BROADCAST_BATCHES = gql`
  mutation retryFailedPushBroadcastBatches($id: ID!) {
    retryFailedPushBroadcastBatches(id: $id) {
      ...PushBroadcastData
    }
  }
  ${PUSH_BROADCAST_DATA}
`;

// Texts, destination and category into a new draft: how a one-account test send becomes the real broadcast.
export const DUPLICATE_PUSH_BROADCAST = gql`
  mutation duplicatePushBroadcast($id: ID!) {
    duplicatePushBroadcast(id: $id) {
      ...PushBroadcastData
    }
  }
  ${PUSH_BROADCAST_DATA}
`;

// A scheduled broadcast back to draft, to edit it.
export const UNSCHEDULE_PUSH_BROADCAST = gql`
  mutation unschedulePushBroadcast($id: ID!) {
    unschedulePushBroadcast(id: $id) {
      ...PushBroadcastData
    }
  }
  ${PUSH_BROADCAST_DATA}
`;

// "Send now instead": replaces the scheduled task (the way out of a lost one, isScheduleOverdue).
export const SEND_PUSH_BROADCAST_NOW = gql`
  mutation sendPushBroadcastNow($id: ID!, $expectedVersion: Int!) {
    sendPushBroadcastNow(id: $id, expectedVersion: $expectedVersion) {
      ...PushBroadcastData
    }
  }
  ${PUSH_BROADCAST_DATA}
`;
